import { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2, AlertCircle, X, RefreshCw } from 'lucide-react';
import { Progress } from '../../../../foundation/altzero_base/base/components/ui/progress';
import { billEmailService } from '../../services/billEmailService';
import type { SyncJob } from '../../services/billEmailService';

const POLL_MS = 1500;
/** How long a finished card lingers before it removes itself. */
const AUTO_DISMISS_MS = 6000;
/**
 * Consecutive failed polls (~15 s) before the card admits it cannot reach the
 * server and offers dismiss. Without this a lasting 500 or an expired session
 * would leave an undismissable card and a locked Sync button.
 */
const POLL_FAILS_BEFORE_ESCAPE = 10;

type Props = {
  /** null renders nothing — the parent clears it to unmount the card. */
  jobId: string | null;
  /**
   * Fired once when the job reaches a terminal state. `job` is null when the
   * server no longer knows about it (restart / eviction), in which case the
   * counts are simply unknown — the list still needs refreshing either way.
   */
  onFinished: (job: SyncJob | null) => void;
  onDismiss: () => void;
};

type Terminal = { kind: 'done'; job: SyncJob } | { kind: 'error'; message: string } | { kind: 'gone' };

/**
 * Bottom-right progress card for a Gmail sync run.
 *
 * The job it polls is stored server-side (bv_email_sync_job), so the card
 * survives a reload and a run the server lost reports as a failure rather than
 * a silent "complete". "Job not found" is still handled: the card says so and
 * refreshes the list, never sitting at 40% forever.
 */
export function SyncProgressCard({ jobId, onFinished, onDismiss }: Props) {
  const [job, setJob] = useState<SyncJob | null>(null);
  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [pollFailing, setPollFailing] = useState(false);

  // Kept in refs so the poll effect depends only on jobId and is never torn
  // down and re-armed by a parent re-render mid-sync.
  const onFinishedRef = useRef(onFinished);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

  useEffect(() => {
    if (!jobId) return;
    setJob(null);
    setTerminal(null);
    setPollFailing(false);

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;

    const finish = (t: Terminal) => {
      if (cancelled) return;
      setTerminal(t);
      onFinishedRef.current(t.kind === 'done' ? t.job : null);
    };

    const poll = async () => {
      const { success, data, gone, error } = await billEmailService.getSyncJob(jobId);
      if (cancelled) return;

      if (gone) return finish({ kind: 'gone' });
      if (!success || !data) {
        // A transient network blip must not kill the card — keep polling.
        console.warn('[inbox] sync progress poll failed:', error);
        failures += 1;
        if (failures >= POLL_FAILS_BEFORE_ESCAPE) setPollFailing(true);
        timer = setTimeout(poll, POLL_MS);
        return;
      }

      failures = 0;
      setPollFailing(false);
      setJob(data);
      if (data.state === 'done') return finish({ kind: 'done', job: data });
      if (data.state === 'error') return finish({ kind: 'error', message: data.error ?? 'Sync failed' });
      timer = setTimeout(poll, POLL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  // Successful runs clear themselves; failures stay until dismissed so the
  // message can actually be read.
  useEffect(() => {
    if (!terminal || terminal.kind === 'error') return;
    const t = setTimeout(() => onDismissRef.current(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [terminal]);

  if (!jobId) return null;

  const counting = !terminal && (!job || job.state === 'counting' || job.total === null);
  const percent = job?.total && job.total > 0
    ? Math.min(100, Math.round((job.processed / job.total) * 100))
    : 0;

  return (
    <div
      data-testid="sync-progress"
      data-state={terminal?.kind ?? (counting ? 'counting' : 'running')}
      role="status"
      aria-live="polite"
      className="fixed bottom-5 right-5 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-xl p-3.5"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {terminal?.kind === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
          ) : terminal ? (
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin shrink-0" />
          )}
          <div className="min-w-0">
            <p data-testid="sync-progress-title" className="text-sm font-medium text-gray-900 truncate">
              {terminal?.kind === 'error' ? 'Sync failed' : terminal ? 'Sync complete' : 'Syncing invoice emails'}
            </p>
            {job?.label && <p className="text-xs text-gray-500 truncate">{job.label}</p>}
          </div>
        </div>
        {/* No dismiss while the run is live: the card is the only sign a sync
            is going, and it now survives a reload, so it stays until the end.
            The exception is a card that has lost the server. */}
        {(terminal || pollFailing) && (
          <button data-testid="sync-progress-dismiss" onClick={onDismiss} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {terminal?.kind === 'error' ? (
        <>
          <p data-testid="sync-progress-error" className="text-xs text-red-600">{terminal.message}</p>
          {/* A stopped run still added emails; say how far it got. */}
          {job && job.processed > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Got through {job.processed}{job.total ? ` of ${job.total}` : ''} · {job.ingested} new
            </p>
          )}
        </>
      ) : terminal?.kind === 'gone' ? (
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <RefreshCw className="h-3 w-3" /> Sync status is no longer available — list refreshed.
        </p>
      ) : (
        <>
          {counting ? (
            // No total yet: an indeterminate sweep, since a 0% determinate bar
            // reads as "stuck" rather than "still counting".
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full w-1/3 rounded-full bg-blue-500 animate-pulse" />
            </div>
          ) : (
            <Progress value={terminal ? 100 : percent} className="h-2 bg-gray-100" />
          )}

          <p data-testid="sync-progress-counts" className="text-xs text-gray-500 mt-1.5">
            {counting ? (
              'Checking Gmail for messages...'
            ) : (
              <>
                {terminal ? job?.total ?? 0 : job?.processed ?? 0} / {job?.total ?? 0} checked
                {' · '}
                <span className="text-green-700 font-medium">{job?.ingested ?? 0} new</span>
                {(job?.skipped ?? 0) > 0 && ` · ${job?.skipped} already had`}
                {(job?.errored ?? 0) > 0 && (
                  <span className="text-red-600 font-medium">{` · ${job?.errored} failed`}</span>
                )}
              </>
            )}
          </p>

          {/* A message that failed is not a message that was checked and passed
              over. Naming it here is what job 65 lacked: the run reported clean
              counts while an invoice had been dropped. */}
          {(job?.errored ?? 0) > 0 && (
            <p data-testid="sync-progress-errored" className="text-xs text-red-600 mt-1">
              {job?.errored} message{(job?.errored ?? 0) === 1 ? '' : 's'} could not be added — check the server log for the message ids.
            </p>
          )}

          {pollFailing && (
            <p data-testid="sync-progress-poll-failing" className="text-xs text-amber-700 mt-1">
              Can't reach the server for progress — still retrying. The sync itself may still be running.
            </p>
          )}

          {/* A cap that stopped the sweep must be stated, not implied by a full bar. */}
          {job?.truncated && (
            <p data-testid="sync-progress-truncated" className="text-xs text-amber-700 mt-1">
              Stopped at the newest {job.total} matches — older ones were not ingested.
            </p>
          )}
        </>
      )}
    </div>
  );
}
